/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get */
// Extends From
import Service, { service } from '@ember/service';
// Ember Imports
import { computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
// External Imports
import Options from '@linc-technologies/linced-common/enums/options';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import {
  pluralize,
  singularize,
} from '@linc-technologies/linced-common/utils/inflector';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';

export default class StringsService extends Service {
  @service
  options;

  @service
  features;

  @service
  schoolSchema;

  @optionAlias('core:groups:homeroomLabel')
  homeroomLabel;

  @computed('homeroomLabel')
  get homeroom() {
    return isEmpty(this.homeroomLabel) ? 'Class' : this.homeroomLabel;
  }

  @computed('homeroom')
  get homerooms() {
    return pluralize(this.homeroom);
  }

  // Learner Tracking Flags
  @optionAlias('core:learnerFlags:redLabel')
  learnerFlagRedLabel;

  @computed('learnerFlagRedLabel')
  get learnerFlagRed() {
    return isEmpty(this.learnerFlagRedLabel) ? 'Red' : this.learnerFlagRedLabel;
  }

  @optionAlias('core:learnerFlags:blueLabel')
  learnerFlagBlueLabel;

  @computed('learnerFlagBlueLabel')
  get learnerFlagBlue() {
    return isEmpty(this.learnerFlagBlueLabel)
      ? 'Blue'
      : this.learnerFlagBlueLabel;
  }

  @optionAlias('core:learnerFlags:greenLabel')
  learnerFlagGreenLabel;

  @computed('learnerFlagGreenLabel')
  get learnerFlagGreen() {
    return isEmpty(this.learnerFlagGreenLabel)
      ? 'Green'
      : this.learnerFlagGreenLabel;
  }

  // Timetable / attendance strings
  @optionAlias(Options.ATTENDANCE_PERIODS_LABEL)
  timetablePeriodsLabel;

  @computed('timetablePeriodsLabel')
  get timetablePeriods() {
    return isEmpty(this.timetablePeriodsLabel)
      ? 'periods'
      : this.timetablePeriodsLabel;
  }

  @computed('timetablePeriods')
  get timetablePeriod() {
    return singularize(this.timetablePeriods);
  }

  // Progression strings
  @optionAlias('progress:strings:wellAbove')
  progressWellAboveLabel;

  @computed('progressWellAboveLabel')
  get progressWellAbove() {
    return isEmpty(this.progressWellAboveLabel)
      ? 'well above'
      : this.progressWellAboveLabel;
  }

  @optionAlias('progress:strings:above')
  progressAboveLabel;

  @computed('progressAboveLabel')
  get progressAbove() {
    return isEmpty(this.progressAboveLabel) ? 'above' : this.progressAboveLabel;
  }

  @optionAlias('progress:strings:at')
  progressAtLabel;

  @computed('progressAtLabel')
  get progressAt() {
    return isEmpty(this.progressAtLabel) ? 'at' : this.progressAtLabel;
  }

  @optionAlias('progress:strings:below')
  progressBelowLabel;

  @computed('progressBelowLabel')
  get progressBelow() {
    return isEmpty(this.progressBelowLabel) ? 'below' : this.progressBelowLabel;
  }

  @optionAlias('progress:strings:wellBelow')
  progressWellBelowLabel;

  @computed('progressWellBelowLabel')
  get progressWellBelow() {
    return isEmpty(this.progressWellBelowLabel)
      ? 'well below'
      : this.progressWellBelowLabel;
  }

  // Progression Descriptions

  @optionAlias('progress:progress:detail:well-above')
  progressWellAboveDescription;

  @computed('progressWellAboveDescription')
  get progressWellAboveDesc() {
    return isEmpty(this.progressWellAboveDescription)
      ? '-'
      : this.progressWellAboveDescription;
  }

  @optionAlias('progress:progress:detail:above')
  progressAboveDescription;

  @computed('progressAboveDescription')
  get progressAboveDesc() {
    return isEmpty(this.progressAboveDescription)
      ? '-'
      : this.progressAboveDescription;
  }

  @optionAlias('progress:progress:detail:at')
  progressAtDescription;

  @computed('progressAtDescription')
  get progressAtDesc() {
    return isEmpty(this.progressAtDescription)
      ? '-'
      : this.progressAtDescription;
  }

  @optionAlias('progress:progress:detail:below')
  progressBelowDescription;

  @computed('progressBelowDescription')
  get progressBelowDesc() {
    return isEmpty(this.progressBelowDescription)
      ? '-'
      : this.progressBelowDescription;
  }

  @optionAlias('progress:progress:detail:well-below')
  progressWellBelowDescription;

  @computed('progressWellBelowDescription')
  get progressWellBelowDesc() {
    return isEmpty(this.progressWellBelowDescription)
      ? '-'
      : this.progressWellBelowDescription;
  }

  // Effort strings

  @optionAlias('progress:effortterminology:one')
  effortWellBelowLabel;

  @computed('effortWellBelowLabel')
  get effortWellBelow() {
    return isEmpty(this.effortWellBelowLabel)
      ? 'below'
      : this.effortWellBelowLabel;
  }

  @optionAlias('progress:effortterminology:two')
  effortBelowLabel;

  @computed('effortBelowLabel')
  get effortBelow() {
    return isEmpty(this.effortBelowLabel) ? 'below' : this.effortBelowLabel;
  }

  @optionAlias('progress:effortterminology:three')
  effortAtLabel;

  @computed('effortAtLabel')
  get effortAt() {
    return isEmpty(this.effortAtLabel) ? 'above' : this.effortAtLabel;
  }

  @optionAlias('progress:effortterminology:four')
  effortAboveLabel;

  @computed('effortAboveLabel')
  get effortAbove() {
    return isEmpty(this.effortAboveLabel) ? 'above' : this.effortAboveLabel;
  }

  @optionAlias('progress:effortterminology:five')
  effortWellAboveLabel;

  @computed('effortWellAboveLabel')
  get effortWellAbove() {
    return isEmpty(this.effortWellAboveLabel)
      ? 'above'
      : this.effortWellAboveLabel;
  }

  @optionAlias('core:groups:teamLabel')
  teamLabel;

  @computed('teamLabel')
  get team() {
    return isEmpty(this.teamLabel) ? 'Team' : this.teamLabel;
  }

  @optionAlias('core:studentGoalSetting:plpFullLabel')
  plpFullLabelValue;

  @computed('plpFullLabelValue')
  get plpFullLabel() {
    return isEmpty(this.plpFullLabelValue)
      ? 'Personal Learning Plan'
      : this.plpFullLabelValue;
  }

  @optionAlias('core:studentGoalSetting:plpShortLabel')
  plpShortLabelValue;

  @computed('plpShortLabelValue')
  get plpShortLabel() {
    return isEmpty(this.plpShortLabelValue) ? 'PLP' : this.plpShortLabelValue;
  }

  @optionAlias('core:labels:goals')
  goalsLabelValue;

  @computed('goalsLabelValue')
  get goalsLabel() {
    return isEmpty(this.goalsLabelValue) ? 'Goals' : this.goalsLabelValue;
  }

  @optionAlias('core:labels:topics')
  topicLabelValue;

  @computed('topicLabelValue')
  get topicLabel() {
    return isEmpty(this.topicLabelValue) ? 'Topics' : this.topicLabelValue;
  }

  @optionAlias('core:labels:topic')
  singularTopicLabelValue;

  @computed('singularTopicLabelValue')
  get singularTopicLabel() {
    return isEmpty(this.singularTopicLabelValue)
      ? 'Topic'
      : this.singularTopicLabelValue;
  }

  @optionAlias('core:labels:planning-core')
  planningCoreLabelValue;

  @computed('planningCoreLabelValue')
  get planningCoreLabel() {
    return isEmpty(this.planningCoreLabelValue)
      ? 'Core'
      : this.planningCoreLabelValue;
  }

  @optionAlias('core:labels:planning-prior')
  planningPriorLabelValue;

  @computed('planningPriorLabelValue')
  get planningPriorLabel() {
    return isEmpty(this.planningPriorLabelValue)
      ? 'Prior'
      : this.planningPriorLabelValue;
  }

  @optionAlias('core:labels:planning-future')
  planningFutureLabelValue;

  @computed('planningFutureLabelValue')
  get planningFutureLabel() {
    return isEmpty(this.planningFutureLabelValue)
      ? 'Future'
      : this.planningFutureLabelValue;
  }

  // People schema strings
  // accessArrangementsField obtains the access arrangements schema field for
  // learners.
  get accessArrangementsField() {
    return this.schoolSchema.peekField(
      PersonType.LEARNER,
      'core:accessArrangements',
    );
  }

  // People schema strings
  // permissionAlertsField obtains the access permissionAlert schema field for
  // learners.
  get permissionAlertsField() {
    return this.schoolSchema.peekField(
      PersonType.LEARNER,
      'core:permissionAlert',
    );
  }

  // accessArrangements returns the learner access arrangements label as
  // set in the field's schema, or returns the default.
  @computed('accessArrangementsField.label')
  get accessArrangements() {
    return this.get('accessArrangementsField.label') ?? 'Access Arrangements';
  }

  // permissionAlert returns the learner permission alert label as
  // set in the field's schema, or returns the default.
  @computed('permissionAlertsField.label')
  get permissionAlert() {
    return this.get('permissionAlertsField.label') ?? 'Permission Alert';
  }

  // Finance strings
  @computed('features.sapFinance')
  get financeLedger() {
    if (this.features.sapFinance) {
      return 'Fee Category';
    }
    return 'Ledger';
  }

  @computed('financeLedger')
  get financeLedgers() {
    return pluralize(this.financeLedger);
  }

  @computed('features.sapFinance')
  get financeLedgerItem() {
    if (this.features.sapFinance) {
      return 'Fee Mapping';
    }
    return 'Ledger Item';
  }

  @computed('financeLedgerItem')
  get financeLedgerItems() {
    return pluralize(this.financeLedgerItem);
  }

  @computed('features.sapFinance')
  get financeLedgerOrItem() {
    if (this.features.sapFinance) {
      return 'Fee Category / Mapping';
    }
    return 'Ledger / Item';
  }
}
